import React from 'react'
import {Box, Card, Chip, Skeleton, Stack, Tooltip, Typography} from '@mui/material'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import PhotoIcon from '@mui/icons-material/Photo'
import TodayIcon from '@mui/icons-material/Today'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import moment from 'moment'
import styles from '@containers/monitoringPlatform/photoFixation/list/sx'
import {IAccountCameraSnapshot, IDeviceCameraSnapshot} from '@containers/monitoringPlatform/photoFixation/types'
import {CAMERA_POSITION_LABELS} from '@containers/cameras/select/selectCameraPosition/constants'
import LinkToSnapshotsByTime from '@containers/cameras/links/linkToSnapshotsByTime/LinkToSnapshotsByTime'
import {DATE_FORMAT_CAMERAS} from '@containers/cameras/constants/dateFormat'

const MAX_PREVIEW_CAMERAS = 4

const renderLastPhoto = (lastPhotoAt: string | null) => {
	if (!lastPhotoAt) {
		return (
			<Typography variant="caption" fontWeight={700} color="text.disabled">
				ще не було фото
			</Typography>
		)
	}
	return (
		<Tooltip title={moment(lastPhotoAt).format('DD.MM.YYYY HH:mm')}>
			<Typography variant="caption" fontWeight={700} color="text.secondary">
				{moment(lastPhotoAt).fromNow()}
			</Typography>
		</Tooltip>
	)
}

const StatValue = ({isLoading, children}: {isLoading: boolean, children: React.ReactNode}) => (
	isLoading
		? <Skeleton variant="text" width={28} sx={{fontSize: 12}}/>
		: <Typography variant="caption" fontWeight={700}>{children}</Typography>
)

const CameraRow = ({camera, isStatsLoading}: {camera: IDeviceCameraSnapshot, isStatsLoading: boolean}) => {
	const position = camera.cameraPosition
		? (CAMERA_POSITION_LABELS[camera.cameraPosition] ?? camera.cameraPosition)
		: null

	return (
		<Box sx={{
			p: 1,
			borderRadius: 1,
			border: '1px solid',
			borderColor: 'divider',
			mb: 0.5,
		}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<PhotoCameraIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
					<Typography variant="caption" fontWeight={700} noWrap>
						{camera.cameraId}
					</Typography>
					{camera.cameraCounter != null && (
						<Typography variant="caption" color="text.disabled">
							· ch{camera.cameraCounter}
						</Typography>
					)}
				</Stack>
				{position && (
					<Chip
						label={position}
						size="small"
						variant="outlined"
						sx={{height: 18, fontSize: 10}}
					/>
				)}
			</Stack>

			<Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<TodayIcon sx={{fontSize: 12, color: 'primary.main'}}/>
					<Typography variant="caption" color="text.secondary">сьогодні:</Typography>
					<StatValue isLoading={isStatsLoading}>{camera.todayPhotos}</StatValue>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<PhotoIcon sx={{fontSize: 12, color: 'text.secondary'}}/>
					<Typography variant="caption" color="text.secondary">за 30 днів:</Typography>
					<StatValue isLoading={isStatsLoading}>{camera.monthPhotos.toLocaleString()}</StatValue>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<AccessTimeIcon sx={{fontSize: 12, color: 'text.secondary'}}/>
					{isStatsLoading
						? <Skeleton variant="text" width={60} sx={{fontSize: 12}}/>
						: renderLastPhoto(camera.lastPhotoAt)}
				</Stack>
			</Stack>
		</Box>
	)
}

interface IProps {
	snapshot: IAccountCameraSnapshot
	accountId: number
	isStatsLoading: boolean
	isPreviewsLoading: boolean
}

export const PhotoFixationCard = ({snapshot, accountId, isStatsLoading, isPreviewsLoading}: IProps) => {
	const previewCameras = snapshot.cameras.slice(0, MAX_PREVIEW_CAMERAS)

	const monthPhotos = snapshot.cameras.reduce((sum, c) => sum + (c.monthPhotos || 0), 0)
	const todayPhotos = snapshot.cameras.reduce((sum, c) => sum + (c.todayPhotos || 0), 0)

	const dateFrom = moment().startOf('day').format(DATE_FORMAT_CAMERAS)
	const dateTo = moment().format(DATE_FORMAT_CAMERAS)

	return (
		<Card variant="outlined" sx={styles.card}>
			<Box sx={styles.previewBox}>
				<Box sx={styles.statusDot(snapshot.isActive)}/>

				<Stack
					direction="row"
					alignItems="flex-start"
					justifyContent="flex-start"
					flexWrap="wrap"
					sx={{width: '100%', height: '100%'}}
				>
					{previewCameras.map((camera, i) => (
						<Box
							key={camera.cameraId || i}
							sx={styles.previewSlot(previewCameras.length)}
						>
							{isPreviewsLoading ? (
								<Skeleton variant="rectangular" width="100%" height="100%"/>
							) : camera.previewUrl ? (
								<Box
									component="img"
									src={camera.previewUrl}
									sx={{
										width: '100%',
										height: '100%',
										objectFit: 'cover'
									}}
								/>
							) : (
								<PhotoCameraIcon sx={{color: 'text.disabled', fontSize: 32}}/>
							)}
						</Box>
					))}
				</Stack>

				<Box sx={styles.cameraBadge}>
					{snapshot.cameras.length} кам.
				</Box>
			</Box>

			<Box sx={{p: 2}}>
				<Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
					<Typography variant="subtitle1" sx={{fontWeight: 700, lineHeight: 1.2}}>
						{snapshot.licencePlate || '—'}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						ID {snapshot.deviceId}
					</Typography>
				</Box>

				<Stack direction="row" alignItems="center" spacing={1} sx={{mb: 1}}>
					<Chip
						label={snapshot.cameraType}
						size="small"
						sx={styles.brandChip(snapshot.cameraType)}
					/>
					<Typography variant="caption" color="text.secondary">
						{snapshot.isActive ? 'В мережі' : 'Активності немає'}
					</Typography>
				</Stack>

				{snapshot.hostName && snapshot.hostName !== 'null' && (
					<Typography variant="caption" color="text.disabled" display="block" mb={1} noWrap>
						{snapshot.hostName}
					</Typography>
				)}

				<Stack direction="row" spacing={2} mb={1.5}>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<TodayIcon sx={{fontSize: 14, color: 'primary.main'}}/>
						<Typography variant="caption" color="text.secondary">
							Сьогодні:
						</Typography>
						<StatValue isLoading={isStatsLoading}>{todayPhotos.toLocaleString()}</StatValue>
					</Stack>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<PhotoIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
						<Typography variant="caption" color="text.secondary">
							За 30 днів:
						</Typography>
						<StatValue isLoading={isStatsLoading}>{monthPhotos.toLocaleString()}</StatValue>
					</Stack>
				</Stack>

				<Box>
					{snapshot.cameras.map((camera) => (
						<CameraRow key={camera.cameraId} camera={camera} isStatsLoading={isStatsLoading}/>
					))}
				</Box>

				<Box sx={{mt: 1.5}}>
					<LinkToSnapshotsByTime
						accountId={accountId}
						deviceId={snapshot.deviceId}
						dateFrom={dateFrom}
						dateTo={dateTo}
					/>
				</Box>
			</Box>
		</Card>
	)
}
